import type { components } from '@/generated/contracts/api-v1';
import type { Story } from '@/types/cmsTypes';
import type { ProjectInstance, SupabaseProjectInstance } from '@/types/projectTypes';
import type { SupabaseSceneOrigin } from '@/types/sceneTypes';
import { safeParseJson } from '@/utils/safeParseJson';
import { convertToProjectInstance, convertToSceneOrigin, convertSceneOriginToStory } from '@/utils/dataConverters';
import { fetchScreenshotsByProject, fetchScreenshotsBySceneOrigin, isUsableScreenshotUrl, pickNewestScreenshotByKey, type VisualContentCache } from '@/utils/sceneScreenshots';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll, listAllIn } from '@/infrastructure/api/v1/lists';
import { ApiProblem } from '@/infrastructure/api/v1/problem';
import { routeMissing } from '@/infrastructure/api/v1/screenReads';
import { StoryDurationService } from '@/modules/storyData/StoryDurationService';
import { projectToLegacy, type ProjectDto } from '@/infrastructure/api/v1/mappers/projects';
import { storyToLegacy, type StoryDto } from '@/infrastructure/api/v1/mappers/stories';
import { imageToLegacy, type ImageDto } from '@/infrastructure/api/v1/mappers/images';
import type { SceneDto } from '@/infrastructure/api/v1/mappers/scenes';
import projectPlaceholder from '@/assets/project-historical.jpg';

type ProjectWorkspaceStory = components['schemas']['ProjectWorkspaceStoryDto'];

// Legacy rows carry jsonb as JSON text, as the generic route sent it, whatever the Supabase row types say
export const toProjectRow = (dto: ProjectDto) => projectToLegacy(dto) as unknown as SupabaseProjectInstance;
export const toStoryRow = (dto: StoryDto) => storyToLegacy(dto) as unknown as SupabaseSceneOrigin;

export const newestImageFirst = (a: ImageDto, b: ImageDto) => Date.parse(b.createdAt) - Date.parse(a.createdAt);

export type ProjectPageStatus = 'draft' | 'live' | 'archived' | 'pending' | 'locked' | 'template';

const STATUS_PRIORITY: Record<ProjectPageStatus, number> = { live: 4, pending: 3, template: 2.5, draft: 2, archived: 1, locked: 0 };

export function highestStatus(statuses: Iterable<ProjectPageStatus>): ProjectPageStatus {
  let highest: ProjectPageStatus = 'draft';
  for (const status of statuses) if (STATUS_PRIORITY[status] > STATUS_PRIORITY[highest]) highest = status;
  return highest;
}

export interface ProjectHero {
  url: string;
  crop: unknown;
}

export interface ProjectPageData {
  project: ProjectInstance;
  stories: Story[];
  storiesError: Error | null;
  // Undefined when the cover could not be read, so the page keeps what it shows
  hero: ProjectHero | undefined;
  // Null when the versions could not be read: unknown, not draft
  status: ProjectPageStatus | null;
  statusUnknown: boolean;
  sceneIds: string[];
  imageIds: string[];
  sceneIdsComplete: boolean;
  imageIdsComplete: boolean;
}

export type ProjectPageLoad =
  | { kind: 'ok'; data: ProjectPageData }
  | { kind: 'not-found' }
  | { kind: 'error'; error: Error };

const isNotFound = (error: ApiProblem) => error.status === 404 || error.status === 403;

const heroOf = (dto: ImageDto): ProjectHero => {
  const image = imageToLegacy(dto);
  const metadata = safeParseJson<Record<string, unknown>>(image.image_metadata);
  return { url: image.image_cropped_url || image.image_original_url || projectPlaceholder, crop: metadata?.crop || null };
};

// Version number descending with unnumbered versions first, as the versions read gave them
const byVersionDesc = (a: SceneDto, b: SceneDto): number => {
  if (a.versionNumber === b.versionNumber) return 0;
  if (a.versionNumber == null) return -1;
  if (b.versionNumber == null) return 1;
  return b.versionNumber - a.versionNumber;
};

/** The status a story card shows, from its versions: locked, then live, pending, template, draft, archived. */
function storyStatus(versions: SceneDto[]): ProjectPageStatus {
  const has = (status: ProjectPageStatus) => versions.some(version => version.status === status);
  const order: ProjectPageStatus[] = ['locked', 'live', 'pending', 'template', 'draft', 'archived'];
  return order.find(has) ?? 'draft';
}

/**
 * The newest usable cover screenshot of each key among the user's versions that have something visual placed, as the
 * page's screenshot reads picked them.
 */
function screenshotsOf(versions: SceneDto[], userId: string, visual: Set<string>, keyOf: (version: SceneDto) => string | null) {
  const rows = versions
    .filter(version => version.userUniqueId === userId && isUsableScreenshotUrl(version.coverImgUrl))
    .map(version => ({ key: keyOf(version), scene_instance_id: version.uniqueId, scene_instance_cover_img_url: version.coverImgUrl, updated_at: version.updatedAt }));
  return pickNewestScreenshotByKey(rows, row => row.key, visual);
}

/**
 * The owner's project page in one read (getProjectWorkspace): the project and its cover, its stories with their
 * covers, and each story card's status, length, anchors, drafts and last edit, worked out from its versions. A backend
 * without that read (rolled back to before it) is read part by part, as before it.
 */
export async function loadProjectPage(id: string, userId: string): Promise<ProjectPageLoad> {
  try {
    const { data: workspace, error } = await toResult(v1.GET('/api/v1/projects/{projectUniqueId}/workspace', { params: { path: { projectUniqueId: id } } }));
    if (routeMissing(error)) return await loadProjectPagePartByPart(id, userId);
    if (error) {
      console.error('Error fetching project:', error);
      return isNotFound(error) ? { kind: 'not-found' } : { kind: 'error', error };
    }
    // Only the signed-in user's own project, as the old user_id filter
    if (workspace.project.userUniqueId !== userId) return { kind: 'not-found' };
    const projectRow = toProjectRow(workspace.project);
    const project = convertToProjectInstance(projectRow);

    const versions = workspace.stories.flatMap(item => item.scenes);
    const visual = new Set(workspace.stories.flatMap(item => item.figures.visualSceneUniqueIds));
    const sceneIds = versions.map(version => version.uniqueId);

    const imageIds: string[] = [];
    let hero: ProjectHero;
    const heroDto = workspace.images[0];
    if (heroDto) {
      imageIds.push(heroDto.uniqueId);
      hero = heroOf(heroDto);
    } else {
      hero = { url: screenshotsOf(versions, userId, visual, () => id).get(id) ?? projectPlaceholder, crop: null };
    }

    const screenshots = screenshotsOf(versions, userId, visual, version => version.storyUniqueId);
    let totalDurationSeconds = 0;
    const stories = workspace.stories.map((item: ProjectWorkspaceStory, index) => {
      const sceneOrigin = toStoryRow(item.story);
      const converted = convertToSceneOrigin(sceneOrigin);
      // The story's covers, newest first: the first is the one its card shows
      imageIds.push(...item.images.map(image => image.uniqueId));
      const cover = item.images[0] ? heroOf(item.images[0]) : null;
      const story = convertSceneOriginToStory(
        converted,
        sceneOrigin.scene_origin_order_number || index + 1,
        cover?.url || screenshots.get(converted.sceneOriginId) || projectPlaceholder,
        cover?.crop || null,
      );

      // Drafts for the "Open Story" button, newest version first
      const drafts = [...item.scenes].sort(byVersionDesc).filter(version => version.status === 'draft' || version.status === 'template');
      // Project total = sum of the per-story rounded minutes, so the header matches the cards
      totalDurationSeconds += item.figures.durationMinutes * 60;
      return {
        ...story,
        preCalculatedStatus: storyStatus(item.scenes),
        preCalculatedDuration: item.figures.durationMinutes,
        preCalculatedDraftCount: drafts.length,
        preCalculatedDraftSceneInstanceId: drafts[0]?.uniqueId ?? null,
        preCalculatedAnchorTypes: item.figures.anchorCategories,
        preCalculatedUpdatedAt: item.figures.lastEditedAt,
      };
    });

    // When the stored total is stale, have the server recompute it (it is server-maintained)
    if (stories.length > 0 && (projectRow.project_instance_total_duration || 0) !== totalDurationSeconds) {
      await toResult(v1.POST('/api/v1/projects/{projectUniqueId}/refresh-summary', { params: { path: { projectUniqueId: id } } }));
    }

    return {
      kind: 'ok',
      data: {
        project: stories.length > 0 ? { ...project, totalDuration: totalDurationSeconds } : project,
        stories,
        storiesError: null,
        hero,
        status: stories.length > 0 ? highestStatus(stories.map(story => story.preCalculatedStatus)) : 'draft',
        statusUnknown: false,
        sceneIds,
        imageIds,
        sceneIdsComplete: true,
        imageIdsComplete: true,
      },
    };
  } catch (error) {
    console.error('Error fetching project and stories:', error);
    return { kind: 'error', error: error instanceof Error ? error : new Error(String(error)) };
  }
}

async function readStoryCovers(storyIds: string[]) {
  const covers = new Map<string, ProjectHero>();
  const imageIds: string[] = [];
  if (storyIds.length === 0) return { covers, imageIds, failed: false };
  const { data, error } = await listAllIn(storyIds, (chunk, page) => v1.GET('/api/v1/images', {
    params: { query: { sceneOriginUniqueId: chunk, category: ['SceneLandscape'], sort: '-createdAt', ...page } },
  }), { compare: newestImageFirst });
  if (error) console.error('Error batch fetching story images:', error);
  for (const dto of data ?? []) {
    imageIds.push(dto.uniqueId);
    const storyId = imageToLegacy(dto).scene_origin_id;
    if (!covers.has(storyId)) covers.set(storyId, heroOf(dto));
  }
  return { covers, imageIds, failed: Boolean(error) };
}

/** The project page read part by part (getProject, listStories, listImages, the screenshots and the stories' versions), for a backend without getProjectWorkspace. */
async function loadProjectPagePartByPart(id: string, userId: string): Promise<ProjectPageLoad> {
  const visualContent: VisualContentCache = new Map();
  const [projectResult, storiesResult, heroResult, projectScreenshots] = await Promise.all([
    toResult(v1.GET('/api/v1/projects/{projectUniqueId}', { params: { path: { projectUniqueId: id } } })),
    listAll(page => v1.GET('/api/v1/stories', {
      params: { query: { projectUniqueId: [id], userUniqueId: userId, sort: 'orderNumber', ...page } },
    })),
    toResult(v1.GET('/api/v1/images', {
      params: { query: { projectUniqueId: [id], category: ['ProjectLandscape'], sort: 'orderNumber', limit: 1 } },
    })),
    fetchScreenshotsByProject([id], userId, visualContent),
  ]);

  if (projectResult.error) {
    console.error('Error fetching project:', projectResult.error);
    return isNotFound(projectResult.error) ? { kind: 'not-found' } : { kind: 'error', error: projectResult.error };
  }
  // Only the signed-in user's own project, as the old user_id filter
  if (projectResult.data.userUniqueId !== userId) return { kind: 'not-found' };
  const projectRow = toProjectRow(projectResult.data);
  const project = convertToProjectInstance(projectRow);

  const imageIds: string[] = [];
  let hero: ProjectHero | undefined;
  const heroDto = heroResult.data?.items[0];
  if (heroDto) {
    imageIds.push(heroDto.uniqueId);
    hero = heroOf(heroDto);
  } else if (!heroResult.error) {
    hero = { url: projectScreenshots.get(id) ?? projectPlaceholder, crop: null };
  }

  if (storiesResult.error) {
    console.error('Error fetching scene origins:', storiesResult.error);
    return {
      kind: 'ok',
      data: {
        project, stories: [], storiesError: storiesResult.error, hero, status: null, statusUnknown: true,
        sceneIds: [], imageIds, sceneIdsComplete: false, imageIdsComplete: false,
      },
    };
  }

  const sceneOrigins = storiesResult.data.map(toStoryRow);
  const storyIds = sceneOrigins.map(so => so.scene_origin_id);
  // The stories' covers, their screenshots and their versions' durations each need only the story ids: read at once
  const durations = storyIds.length === 0
    ? null
    : new StoryDurationService().batchCalculateStoryDurationsDetailed(storyIds).then(batch => ({ batch, error: null }), (error: unknown) => ({ batch: null, error }));
  const [{ covers, imageIds: coverIds, failed: coversFailed }, screenshots] = await Promise.all([
    readStoryCovers(storyIds),
    fetchScreenshotsBySceneOrigin(storyIds, userId, visualContent),
  ]);
  imageIds.push(...coverIds);
  const imageIdsComplete = !heroResult.error && !coversFailed;

  const stories = sceneOrigins.map((sceneOrigin, index) => {
    const converted = convertToSceneOrigin(sceneOrigin);
    const cover = covers.get(converted.sceneOriginId);
    return convertSceneOriginToStory(
      converted,
      sceneOrigin.scene_origin_order_number || index + 1,
      cover?.url || screenshots.get(converted.sceneOriginId) || projectPlaceholder,
      cover?.crop || null,
    );
  });

  if (stories.length === 0) {
    return {
      kind: 'ok',
      data: { project, stories, storiesError: null, hero, status: 'draft', statusUnknown: false, sceneIds: [], imageIds, sceneIdsComplete: true, imageIdsComplete },
    };
  }

  const { batch, error: durationError } = await durations!;
  if (!batch) {
    console.error('Error in batch duration calculation:', durationError);
    // Unknown, not draft: keep the stored status and make no write from a failed read
    return {
      kind: 'ok',
      data: { project, stories, storiesError: null, hero, status: null, statusUnknown: true, sceneIds: [], imageIds, sceneIdsComplete: false, imageIdsComplete },
    };
  }

  const { storyStatuses, storyDurations, storyDraftInfo, storyAnchorTypes, storyUpdatedAt, totalDurationSeconds, durationsKnown, sceneIds } = batch;

  // When the stored total is stale, have the server recompute it (it is server-maintained)
  if (durationsKnown && (projectRow.project_instance_total_duration || 0) !== totalDurationSeconds) {
    await toResult(v1.POST('/api/v1/projects/{projectUniqueId}/refresh-summary', { params: { path: { projectUniqueId: id } } }));
  }

  return {
    kind: 'ok',
    data: {
      project: durationsKnown ? { ...project, totalDuration: totalDurationSeconds } : project,
      stories: stories.map(story => ({
        ...story,
        preCalculatedStatus: storyStatuses[story.id],
        preCalculatedDuration: durationsKnown ? storyDurations[story.id] : null,
        preCalculatedDraftCount: storyDraftInfo[story.id]?.draftCount,
        preCalculatedDraftSceneInstanceId: storyDraftInfo[story.id]?.draftSceneInstanceId,
        preCalculatedAnchorTypes: durationsKnown ? storyAnchorTypes[story.id] : undefined,
        preCalculatedUpdatedAt: storyUpdatedAt[story.id],
      })),
      storiesError: null,
      hero,
      status: highestStatus(Object.values(storyStatuses)),
      statusUnknown: false,
      sceneIds,
      imageIds,
      sceneIdsComplete: true,
      imageIdsComplete,
    },
  };
}

export async function loadProjectRow(id: string, userId: string): Promise<ProjectInstance | null> {
  const { data, error } = await toResult(v1.GET('/api/v1/projects/{projectUniqueId}', { params: { path: { projectUniqueId: id } } }));
  if (error || data.userUniqueId !== userId) return null;
  return convertToProjectInstance(toProjectRow(data));
}
